import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { IoHomeOutline } from "react-icons/io5";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import ProjeKart from "./ProjeKart";
import ProjeDetay from "./ProjeDetay";
import { useDispatch, useSelector } from "react-redux";
import {
  getProjects,
  getProjectById,
  setSelectedProject,
} from "../../redux/slices/projectSlice";
import ProjeKartLoader from "../../components/loader/ProjeKartLoader";
import ProjeDetayLoader from "../../components/loader/ProjeDetayLoader";
import ServerErrorPage from "../Hatalar/ServerErrorPage";

const Proje = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { projectList, selectedProject, loading, projectDetailLoading, error } = useSelector(
    (state) => state.projects
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const projectRefs = useRef({});
  const isInitialized = useRef(false);

  // Proje listesini memoize et
  const memoizedProjectList = useMemo(() => {
    return projectList.slice().reverse();
  }, [projectList]);

  // Loading durumu için tek bir loader componenti
  const renderLoader = useCallback((count = 3) => {
    return Array.from({ length: count }).map((_, i) => (
      <ProjeKartLoader key={i} />
    ));
  }, []);

  const handleResize = useCallback(() => {
    const mobile = window.innerWidth < 640;
    if (mobile !== isMobile) {
      setIsMobile(mobile);
      if (mobile) {
        dispatch(setSelectedProject(null));
      }
    }
  }, [isMobile, dispatch]);

  // Ana initialization useEffect - tüm başlangıç işlemlerini tek yerde topla
  useEffect(() => {
    if (isInitialized.current) return;
    
    // Resize listener'ı ekle
    handleResize();
    window.addEventListener("resize", handleResize);
    
    // Projeleri çek
    dispatch(getProjects());
    
    // Component mount olduğunda selectedProject'i temizle
    dispatch(setSelectedProject(null));
    
    isInitialized.current = true;
    
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, handleResize]);

  // Masaüstü için varsayılan proje seçimi - sadece bir kez çalışsın
  useEffect(() => {
    if (
      !isMobile && 
      !selectedProject && 
      projectList.length > 0 && 
      isInitialized.current &&
      !loading &&
      !projectDetailLoading // Proje detayı yüklenirken yeni proje seçme
    ) {
      dispatch(getProjectById(projectList[0].id));
    }
  }, [isMobile, projectList.length, selectedProject, dispatch, loading, projectDetailLoading]);

  // Bildirimden gelen proje ID'sine göre scroll yapma ve vurgulama
  useEffect(() => {
    if (
      location.state?.highlightProjectId && 
      projectList.length > 0 && 
      !loading && 
      !projectDetailLoading // Proje detayı yüklenirken yeni proje seçme
    ) {
      const projectId = location.state.highlightProjectId;
      dispatch(getProjectById(projectId));

      // Scroll işlemini biraz geciktir
      setTimeout(() => {
        const projectElement = projectRefs.current[projectId];
        if (projectElement) {
          projectElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
  }, [location.state?.highlightProjectId, projectList.length, dispatch, loading, projectDetailLoading]);

  const handleBack = useCallback(() => {
    dispatch(setSelectedProject(null));
  }, [dispatch]);

  const handleSelectProject = useCallback(
    (project) => {
      if (!project?.id || project.id === selectedProject?.id) return;
      dispatch(getProjectById(project.id));
    },
    [selectedProject?.id, dispatch]
  );

  if (error) return <ServerErrorPage />;

  if (!loading && projectList.length === 0) {
    return (
      <div className="containerMain flex flex-col items-center justify-center min-h-[90vh] gap-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-grayy mb-2">
            Proje bulunamadı
          </h2>
          <p className="text-grayy">Şu anda aktif proje bulunmuyor.</p>
        </div>
        <Link to="/" className="btn-dark text-white text-sm">
          Ana Sayfaya Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="containerMain flex flex-col gap-8 mb-48">
      <div className="baslik flex flex-col gap-4 ">
        <h1 className="text-2xl font-bold">Projeler ({projectList.length}) </h1>
        <div className="text-sm text-grayy flex items-center text gap-2 font-medium">
          <IoHomeOutline className="mr-1 w-4 h-4" />
          <Link to="/" className="text-grayy hover:underline">
            Ana Sayfa
          </Link>
          <MdOutlineKeyboardArrowRight className="w-6 h-6 " />
          <span className="text-darkerGreen font-medium">Projeler</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-8 mt-4 max-h-[500px] min-h-[500px]">
        <div
          className={`w-full sm:w-1/2 space-y-4 overflow-y-auto scrollbar-projects ${
            selectedProject ? "hidden sm:block" : "block"
          }`}
        >
          <div className="flex flex-col gap-6 shadow-cardShadow">
            {loading
              ? renderLoader()
              : memoizedProjectList.map((project) => (
                  <div
                    key={project.id}
                    ref={(el) => (projectRefs.current[project.id] = el)}
                  >
                    <ProjeKart
                      project={project}
                      selectedProject={selectedProject}
                      onSelect={handleSelectProject}
                      isHighlighted={
                        location.state?.highlightProjectId === project.id
                      }
                    />
                  </div>
                ))}
          </div>
        </div>

        <div className="w-full sm:w-1/2 overflow-y-auto scrollbar-projects shadow-cardShadow">
          {projectDetailLoading && !isMobile ? (
            <ProjeDetayLoader />
          ) : selectedProject ? (
            <ProjeDetay project={selectedProject} onBack={handleBack} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Proje;
